/**
 * Icon Map
 *
 * Maps kebab-case icon names to their component implementations.
 */

import * as Icons from "./icons";
import type { IconComponent } from "./create-icon";
import type { IconName } from "./types";

/**
 * Map of kebab-case icon names to their component implementations
 */
export const iconMap: Record<IconName, IconComponent> = {
  // Navigation & Arrows
  "chevron-down": Icons.ChevronDown,
  "chevron-up": Icons.ChevronUp,
  "chevron-left": Icons.ChevronLeft,
  "chevron-right": Icons.ChevronRight,
  "chevron-down-lg": Icons.ChevronDownLg,
  "chevron-up-lg": Icons.ChevronUpLg,
  "chevron-left-lg": Icons.ChevronLeftLg,
  "chevron-right-lg": Icons.ChevronRightLg,
  "arrow-right": Icons.ArrowRight,
  "arrow-left": Icons.ArrowLeft,
  "arrow-up": Icons.ArrowUp,
  "arrow-down": Icons.ArrowDown,
  "expand-all": Icons.ExpandAll,
  "diagonal-arrow-ne": Icons.DiagonalArrowNe,
  "diagonal-arrow-nw": Icons.DiagonalArrowNw,
  "diagonal-arrow-se": Icons.DiagonalArrowSe,
  "diagonal-arrow-sw": Icons.DiagonalArrowSw,
  forward: Icons.Forward,
  start: Icons.Start,
  to: Icons.To,

  // Actions & Controls
  close: Icons.Close,
  menu: Icons.Menu,
  search: Icons.Search,
  plus: Icons.Plus,
  minus: Icons.Minus,
  "external-link": Icons.ExternalLink,
  cancel: Icons.Cancel,
  more: Icons.More,
  logout: Icons.Logout,
  "filter-sliders": Icons.FilterSliders,
  apps: Icons.Apps,
  share: Icons.Share,
  "upload-file": Icons.UploadFile,
  description: Icons.Description,
  delete: Icons.Delete,
  download: Icons.Download,

  // Status
  check: Icons.Check,
  "check-circle": Icons.CheckCircle,
  error: Icons.Error,
  warning: Icons.Warning,
  info: Icons.Info,

  // Social
  facebook: Icons.Facebook,
  instagram: Icons.Instagram,
  linkedin: Icons.Linkedin,
  x: Icons.X,
  youtube: Icons.Youtube,

  // Miscellaneous
  circle: Icons.Circle,
  schedule: Icons.Schedule,
  history: Icons.History,
  star: Icons.Star,
  copyright: Icons.Copyright,
  "rv-hookup": Icons.RvHookup,
  "finance-mode": Icons.FinanceMode,
  "pace-easy": Icons.PaceEasy,
  "pace-balanced": Icons.PaceBalanced,
  "pace-packed": Icons.PacePacked,

  // Security
  fingerprint: Icons.Fingerprint,
  "safety-check": Icons.SafetyCheck,
  "speed-camera": Icons.SpeedCamera,
  visibility: Icons.Visibility,
  "visibility-off": Icons.VisibilityOff,
  lock: Icons.Lock,
  "lock-open": Icons.LockOpen,
  "house-with-shield": Icons.HouseWithShield,

  // People
  "account-circle": Icons.AccountCircle,
  group: Icons.Group,
  "assignment-ind": Icons.AssignmentInd,
  groups: Icons.Groups,
  family: Icons.Family,
  demography: Icons.Demography,
  "person-play": Icons.PersonPlay,
  "person-celebrate": Icons.PersonCelebrate,
  handshake: Icons.Handshake,
  "accessible-forward": Icons.AccessibleForward,

  // Communication
  "captive-portal": Icons.CaptivePortal,
  smartphone: Icons.Smartphone,
  mail: Icons.Mail,
  wifi: Icons.Wifi,
  help: Icons.Help,
  whatsapp: Icons.Whatsapp,
  globe: Icons.Globe,

  // Places & Amenities
  shower: Icons.Shower,
  parking: Icons.Parking,
  "light-bulb": Icons.LightBulb,
  "calendar-today": Icons.CalendarToday,
  "calendar-event": Icons.CalendarEvent,
  map: Icons.Map,
  celebration: Icons.Celebration,
  leaf: Icons.Leaf,
  palette: Icons.Palette,
  anchor: Icons.Anchor,
  "shopping-tag": Icons.ShoppingTag,
  "confirmation-number": Icons.ConfirmationNumber,
  ticket: Icons.Ticket,
  "theater-comedy": Icons.TheaterComedy,
  "service-bell": Icons.ServiceBell,
  luggage: Icons.Luggage,
  "map-pin-review": Icons.MapPinReview,
  curtains: Icons.Curtains,
  "real-estate-agent": Icons.RealEstateAgent,
  landscape: Icons.Landscape,
  "business-center": Icons.BusinessCenter,
  work: Icons.Work,
  houseboat: Icons.Houseboat,
  "home-work": Icons.HomeWork,
  home: Icons.Home,
  scene: Icons.Scene,
  "source-environment": Icons.SourceEnvironment,
  apartment: Icons.Apartment,
  "location-city": Icons.LocationCity,
  storefront: Icons.Storefront,
  festival: Icons.Festival,
  "shopping-bag": Icons.ShoppingBag,
  "ferris-wheel": Icons.FerrisWheel,
  boat: Icons.Boat,
  store: Icons.Store,

  // Food & Drink
  "water-bottle": Icons.WaterBottle,
  "coffee-maker": Icons.CoffeeMaker,
  "menu-book": Icons.MenuBook,
  fastfood: Icons.Fastfood,

  // UI Controls
  layers: Icons.Layers,
  edit: Icons.Edit,
  "sort-by-alpha": Icons.SortByAlpha,
  "grid-view": Icons.GridView,
  "format-list-bulleted": Icons.FormatListBulleted,
  "swap-vert": Icons.SwapVert,
  "filter-list": Icons.FilterList,
  "list-alt": Icons.ListAlt,
  "list-add": Icons.ListAdd,
  "list-added": Icons.ListAdded,

  // Media
  camera: Icons.Camera,
  image: Icons.Image,
  "video-camera-add": Icons.VideoCameraAdd,
  article: Icons.Article,
  pen: Icons.Pen,
  bookmark: Icons.Bookmark,
  "bookmark-add": Icons.BookmarkAdd,
  "bookmark-added": Icons.BookmarkAdded,
  "play-arrow": Icons.PlayArrow,
  pause: Icons.Pause,
  quote: Icons.Quote,
};

/**
 * List of all available icon names for documentation and validation
 */
export const availableIcons = Object.keys(iconMap) as IconName[];
